/**
 * Button - 범용 버튼 컴포넌트
 *
 * @param variant - 'primary' | 'secondary' | 'soft' | 'danger' | 'ghost' 등 스타일 변형
 * @param loading - true이면 스피너 표시 및 비활성화
 * @param leftIcon / rightIcon - 버튼 양쪽에 배치할 아이콘
 * @param href - 지정 시 <button> 대신 next/link(<a>)로 렌더. 페이지 이동 CTA에 사용
 *   (예: "홈으로"). 시맨틱상 이동은 링크, 동작은 버튼이 올바르므로 폴리모픽으로 처리한다.
 */
'use client';

import React, { forwardRef } from 'react';
import Link from 'next/link';
import styles from './Button.module.css';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'soft'
  | 'danger'
  | 'ghost'
  | 'outlined'
  | 'link'
  | 'warning'
  | 'success';
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type ButtonColorScheme =
  | 'brand'
  | 'gray'
  | 'destructive'
  | 'warning'
  | 'success'
  | 'blue'
  | 'teal'
  | 'purple'
  | 'pink'
  | 'orange';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  iconOnly?: boolean;
  colorScheme?: ButtonColorScheme;
  /**
   * 지정 시 <button>이 아니라 next/link(<a>)로 렌더한다.
   * 페이지 이동을 의미하는 CTA(예: "홈으로")에 사용하여 시맨틱을 올바르게 유지한다.
   */
  href?: string;
  children: React.ReactNode;
}

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      loading = false,
      disabled = false,
      leftIcon,
      rightIcon,
      iconOnly = false,
      colorScheme,
      href,
      children,
      className = '',
      ...props
    },
    ref,
  ) => {
    const classNames = [
      styles.button,
      styles[variant],
      styles[size],
      fullWidth ? styles.fullWidth : '',
      loading ? styles.loading : '',
      iconOnly ? styles.iconOnly : '',
      colorScheme ? (styles[`color-${colorScheme}` as keyof typeof styles] ?? '') : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const inner = (
      <>
        {loading && <span className={styles.spinner} />}
        <span className={loading ? styles.hiddenText : styles.content}>
          {leftIcon && <span className={styles.icon}>{leftIcon}</span>}
          {!(iconOnly && (leftIcon || rightIcon)) && <span>{children}</span>}
          {rightIcon && <span className={styles.icon}>{rightIcon}</span>}
        </span>
      </>
    );

    // href가 있으면 시맨틱상 올바른 링크(<a>)로 렌더한다. (페이지 이동 CTA)
    if (href) {
      // button 전용 속성(type 등)은 앵커에 전달하지 않는다.
      const { type: _type, ...anchorProps } =
        props as React.ButtonHTMLAttributes<HTMLButtonElement>;
      void _type;
      return (
        <Link
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={href}
          className={classNames}
          aria-disabled={disabled || loading || undefined}
          {...(anchorProps as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
        >
          {inner}
        </Link>
      );
    }

    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        className={classNames}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {inner}
      </button>
    );
  },
);

Button.displayName = 'Button';

export default Button;
